<!-- #################################################################################### -->
<!-- ###### HERINCO                                                                ###### -->
<!-- ###### @author: Santiago Díaz Marín                                           ###### -->
<!-- ###### @date: Febrero 2026                                                    ###### -->
<!-- #################################################################################### -->

<!-- #################################################################################### -->
<!-- ###### Sección de HTML                                                        ###### -->
<!-- #################################################################################### -->
<template>
  <div class="contenido">
    <!-- Sección de filtros -->
    <section class="d-flex align-center">
      <!-- Filtrar por Nombre -->
      <v-text-field class="me-2 filtro" v-model="buscarNombre" label="Nombre de subzona" outlined dense hide-details
        clearable maxlength="50">
      </v-text-field>
      <!-- Filtrar por Tipo -->
      <v-select class="me-2 filtro" v-model="buscarTipo" :items="opcionesTipo" item-text="text" item-value="value"
        label="Tipo" outlined dense hide-details clearable>
      </v-select>
      <!-- Filtrar por Bodega -->
      <v-autocomplete class="me-2 filtro" v-model="buscarBodega" :items="listaBodegas" item-text="text"
        item-value="value" label="Bodega" outlined dense hide-details clearable no-data-text="Sin resultados">
      </v-autocomplete>

      <v-select class="me-2 filtro" v-model="buscarEstado" :items="opcionesEstado" item-text="text" item-value="value"
        label="Estado" outlined dense hide-details clearable>
      </v-select>
      <!-- Botón agregar -->
      <div>
        <v-tooltip left color="success" :open-on-focus="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn small fab color="success" v-bind="attrs" v-on="on" @click="abrirDialogoCrear()">
              <v-icon>add</v-icon>
            </v-btn>
          </template>
          <span>Agregar</span>
        </v-tooltip>
      </div>
    </section>

    <!-- Tabla de subzonas -->
    <v-data-table class="elevation mt-4" :items="tablaSubZonas.items" :headers="tablaSubZonas.headers"
      :page.sync="tablaSubZonas.pagination.page" :items-per-page.sync="tablaSubZonas.pagination.itemsPerPage"
      :server-items-length="tablaSubZonas.totalPage" :footer-props="tablaSubZonas.footerProps" height="64vh"
      fixed-header :loading="loading" loading-text="Cargando subzonas...">
      <!-- Columna de Tipo -->
      <template v-slot:[`item.tipoSubZona`]="{ item }">
        {{ item.tipoSubZona === 'S' ? 'Picking' : 'Packing' }}
      </template>
      <!-- Columna de Bodega -->
      <template v-slot:[`item.codigoBodega`]="{ item }">
        {{ item.codigoBodega }} - {{ item.nombreBodega }}
      </template>
      <!-- Columna de Zona -->
      <template v-slot:[`item.nombreZonaPrincipal`]="{ item }">
        {{ item.nombreZonaPrincipal || 'Sin zona' }}
      </template>
      <!-- Columna de Estado -->
      <template v-slot:[`item.eliminado`]="{ item }">
        <v-icon :color="item.eliminado === false ? 'success' : 'error'">
          {{ item.eliminado === false ? 'check_circle' : 'cancel' }}
        </v-icon>
        {{ item.eliminado === false ? 'Activo' : 'Inactivo' }}
      </template>
      <!-- Columna de Acciones -->
      <template v-slot:[`item.acciones`]="{ item }">
        <v-btn icon @click="item.eliminado === false && abrirDialogoEditar(item)">
          <v-icon :color="item.eliminado === false ? 'orange' : 'grey'">border_color</v-icon>
        </v-btn>
        <v-btn icon @click="abrirDialogoEstado(item)">
          <v-icon :color="item.eliminado === false ? 'error' : 'success'">
            {{ item.eliminado === false ? 'person_add_disabled' : 'how_to_reg' }}
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Diálogo confirmación cambio de estado -->
    <v-dialog v-model="dialogEstado" persistent max-width="360">
      <v-card>
        <v-card-title class="titulo">
          <span class="text-h6">{{ mensajeEstado }}</span>
        </v-card-title>
        <v-card-text class="espaciado">
          <div class="d-flex justify-end">
            <v-btn class="me-2" text color="error" @click="dialogEstado = false">NO</v-btn>
            <v-btn text color="success" @click="cambiarEstado()">SI</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo crear/editar subzona -->
    <v-dialog v-model="dialogoSubZona" max-width="32rem" persistent>
      <v-card>
        <v-card-title class="titulo">
          <span class="text-h6">{{ tituloDialogo }}</span>
        </v-card-title>
        <v-card-text class="espaciado">
          <v-form ref="formSubZona">
            <div class="d-flex">
              <v-text-field class="me-2" v-model="subZonaSeleccionada.nombreSubZona" label="Nombre*" outlined dense
                :rules="rules.soloLetrasNumeros" @keypress="soloLetrasNumeros($event)" maxlength="50">
              </v-text-field>
              <v-select label="Tipo*" v-model="subZonaSeleccionada.tipoSubZona" :items="opcionesTipo" item-text="text"
                item-value="value" outlined dense :rules="rules.required" :menu-props="{ offsetY: true }">
              </v-select>
            </div>
            <div>
              <v-autocomplete v-model="subZonaSeleccionada.codigoBodega" label="Bodega*" outlined dense
                :rules="rules.required" :items="listaBodegas" item-text="text" item-value="value"
                :menu-props="{ offsetY: true }" no-data-text="Sin resultados" @change="cargarZonasPorBodega"
                :disabled="esEdicion">
              </v-autocomplete>

              <v-autocomplete v-model="subZonaSeleccionada.idZona" label="Zona Principal*" outlined dense
                :rules="esEdicion ? [] : reglasZonaPrincipal" :items="listaZonas" item-text="text" item-value="value"
                :menu-props="{ offsetY: true }"
                :no-data-text="subZonaSeleccionada.codigoBodega ? 'La bodega no tiene zonas asignadas.' : 'Seleccione una bodega'"
                :disabled="esEdicion">
              </v-autocomplete>

            </div>
            <div class="d-flex justify-end mt-4">
              <v-btn class="me-2" text color="error" @click="cerrarDialogo()">Cerrar</v-btn>
              <v-btn text color="success" :disabled="!formularioValido" @click="guardar()">Guardar</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<!-- #################################################################################### -->
<!-- ###### Sección de Script                                                      ###### -->
<!-- #################################################################################### -->
<script>
import { mapMutations, mapState } from "vuex";

export default {
  name: "SubZonas",
  data() {
    return {
      loading: false,
      esEdicion: false,
      dialogoSubZona: false,
      dialogEstado: false,
      tituloDialogo: "",
      mensajeEstado: "",

      buscarNombre: "",
      buscarTipo: "",
      buscarBodega: null,
      buscarEstado: null,

      subZonaSeleccionada: {
        idSubZona: null,
        nombreSubZona: "",
        tipoSubZona: null,
        codigoBodega: null,
        idZona: null,
        idEmpresa: null,
        eliminado: false,
      },

      tablaSubZonas: {
        items: [],
        totalPage: 0,
        pagination: { page: 1, itemsPerPage: 10 },
        headers: [
          { text: "NOMBRE", value: "nombreSubZona", align: "left", sortable: false },
          { text: "TIPO", value: "tipoSubZona", align: "center", sortable: false },
          { text: "BODEGA", value: "codigoBodega", align: "left", sortable: false },
          { text: "ZONA", value: "nombreZonaPrincipal", align: "left", sortable: false },
          { text: "ESTADO", value: "eliminado", align: "center", sortable: false },
          { text: "ACCIONES", value: "acciones", align: "center", sortable: false }
        ],
        footerProps: {
          'items-per-page-options': [10, 20, 50],
          'items-per-page-text': 'Items por página:',
          'show-current-page': true,
          'show-first-last-page': true
        }
      },

      listaBodegas: [],
      listaZonas: [],

      opcionesTipo: [
        { text: "Picking", value: "S" },
        { text: "Packing", value: "E" }
      ],

      opcionesEstado: [
        { text: "Activo", value: false },
        { text: "Inactivo", value: true }
      ],

      rules: {
        required: [v => (v !== null && v !== undefined && v !== "") || "Campo obligatorio."],
        soloLetrasNumeros: [
          v => (v !== null && v !== undefined && v !== "") || "Campo obligatorio.",
          v => /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s]+$/.test(v) || "Solo se permiten letras, números y tildes."
        ],
      }
    };
  },

  computed: {
    ...mapState(["auth", "enterprise"]),

    reglasZonaPrincipal() {
      return [
        v => (v !== null && v !== undefined && v !== "") || "Campo obligatorio.",
        () => this.listaZonas.length > 0 || "La bodega no tiene zonas asignadas."
      ];
    },

    /**
     * Obtiene el usuario logueado en mayúsculas.
     * @returns {string} Usuario actual en mayúsculas.
     */
    usuarioLogueado() {
      return this.auth.username.toUpperCase();
    },

    /**
     * Valida si todos los campos obligatorios están completos.
     * @returns {boolean} True si el formulario es válido.
     */
    formularioValido() {
      const base = this.subZonaSeleccionada.nombreSubZona?.trim() && this.subZonaSeleccionada.tipoSubZona;
      if (this.esEdicion) return !!base;
      return !!(base && this.subZonaSeleccionada.codigoBodega && this.subZonaSeleccionada.idZona);
    }
  },

  watch: {
    //Vigila cambios en items por página y resetea a página.
    'tablaSubZonas.pagination.page'() { this.listarSubZonas(); },
    'tablaSubZonas.pagination.itemsPerPage'() {
      this.tablaSubZonas.pagination.page = 1;
      this.listarSubZonas();
    },
    //Vigila cambios en filtros y resetea la paginación.
    'buscarNombre'() { this.resetearPaginaYListar(); },
    'buscarTipo'() { this.resetearPaginaYListar(); },
    'buscarBodega'() { this.resetearPaginaYListar(); },
    'buscarEstado'() { this.resetearPaginaYListar(); }
  },

  created() {
    this.listarBodegas();
    this.listarSubZonas();
  },

  methods: {
    ...mapMutations(["showSuccess", "showError"]),

    //Resetea la paginación a la página 1 y recarga la lista.
    resetearPaginaYListar() {
      this.tablaSubZonas.pagination.page = 1;
      this.listarSubZonas();
    },

    soloLetrasNumeros(event) {
      const regex = /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s]$/;
      if (!regex.test(event.key)) {
        event.preventDefault();
      }
    },

    /**
    * Método para listar las subzonas con filtros aplicados.
    * Realiza una petición GET al backend con parámetros de paginación y búsqueda.
    */
    listarSubZonas() {
      this.loading = true;
      this.$http.get("msa-administration/api/subzonas/listar", {
        params: {
          page: this.tablaSubZonas.pagination.page - 1,
          size: this.tablaSubZonas.pagination.itemsPerPage,
          nombreSubZona: this.buscarNombre,
          tipoSubZona: this.buscarTipo,
          codigoBodega: this.buscarBodega,
          eliminado: this.buscarEstado,
          idEmpresa: this.enterprise.code
        }
      }).then(response => {
        this.tablaSubZonas.items = response.data.content;
        this.tablaSubZonas.totalPage = response.data.totalElements;
      }).catch(error => {
        this.showError(error.response?.data?.message || "Error al cargar las subzonas");
      }).finally(() => { this.loading = false; });
    },

    /**
     * Método para listar las bodegas disponibles para asignar a las subzonas.
     */
    listarBodegas() {
      this.$http.get(`msa-administration/inBodeg/usuario/zona`, {
        params: { codEmpr: this.enterprise.code, codUsua: this.auth.username }
      }).then(response => {
        this.listaBodegas = response.data.map(item => ({
          text: `${item.codBode} - ${item.nomBode}`,
          value: item.codBode
        }));
      }).catch(() => {
        this.showError("Error cargando las bodegas");
      });
    },

    /**
     * Método para cargar las zonas asociadas a la bodega seleccionada.
     * @param {void} Se dispara al cambiar la bodega en el formulario.
     */
    cargarZonasPorBodega() {
      this.subZonaSeleccionada.idZona = null;
      this.listaZonas = [];
      if (!this.subZonaSeleccionada.codigoBodega) return;
      this.$http.get("msa-administration/zonas/listar", {
        params: {
          page: 0,
          size: 100,
          codigoZona: "",
          nombreZona: "",
          codigoBodega: this.subZonaSeleccionada.codigoBodega,
          idEmpresa: this.enterprise.code
        }
      }).then(response => {
        this.listaZonas = response.data.content.map(item => ({
          text: `${item.codigoZona} - ${item.nombreZona}`,
          value: item.idZona
        }));
        this.$refs.formSubZona.validate();
      }).catch(error => {
        this.showError(error.response?.data?.message || "Error al cargar las zonas");
      });
    },

    /**
     * Método para abrir el diálogo de creación de una nueva subzona.
     * Inicializa el formulario vacío y establece el modo creación.
     */
    abrirDialogoCrear() {
      this.clear();
      this.esEdicion = false;
      this.tituloDialogo = "Crear Subzona";
      this.listarBodegas();
      this.dialogoSubZona = true;
    },

    /**
     * Método para abrir el diálogo de edición de una subzona existente.
     * Carga los datos de la subzona seleccionada en el formulario.
     * @param {Object} item - La subzona seleccionada para editar.
     */
    abrirDialogoEditar(item) {
      this.clear();
      this.esEdicion = true;
      this.tituloDialogo = "Editar Subzona";
      this.listaBodegas = [{ text: `${item.codigoBodega} - ${item.nombreBodega}`, value: item.codigoBodega }];
      this.subZonaSeleccionada = {
        idSubZona: item.idSubZona,
        idEmpresa: item.idEmpresa,
        nombreSubZona: item.nombreSubZona,
        tipoSubZona: item.tipoSubZona,
        codigoBodega: item.codigoBodega,
        idZona: item.idZona,
        eliminado: item.eliminado,
      };
      this.$http.get("msa-administration/zonas/listar", {
        params: { page: 0, size: 100, codigoZona: "", nombreZona: "", codigoBodega: item.codigoBodega, idEmpresa: this.enterprise.code }
      }).then(response => {
        this.listaZonas = response.data.content.map(z => ({ text: `${z.codigoZona} - ${z.nombreZona}`, value: z.idZona }));
        this.dialogoSubZona = true;
      }).catch(error => {
        this.showError(error.response?.data?.message || "Error al cargar las zonas");
      });
    },

    /**
     * Método para guardar o actualizar una subzona.
     * Valida el formulario y envía la petición correspondiente al backend.
     */
    guardar() {
      if (!this.$refs.formSubZona.validate()) return;
      this.subZonaSeleccionada.nombreSubZona = this.subZonaSeleccionada.nombreSubZona.trim();
      this.loading = true;
      const url = this.esEdicion ? "actualizar" : "guardar";
      const metodo = this.esEdicion ? "put" : "post";

      const payload = {
        idSubZona: this.subZonaSeleccionada.idSubZona,
        idEmpresa: Number(this.enterprise.code),
        nombreSubZona: this.subZonaSeleccionada.nombreSubZona,
        tipoSubZona: this.subZonaSeleccionada.tipoSubZona,
        codigoBodega: Number(this.subZonaSeleccionada.codigoBodega),
        idZona: this.subZonaSeleccionada.idZona,
        eliminado: this.subZonaSeleccionada.eliminado,
      };

      this.$http[metodo](`msa-administration/api/subzonas/${url}`, payload, {
        params: { actUsua: this.usuarioLogueado }
      })
        .then(() => {
          this.showSuccess(this.esEdicion ? "Subzona actualizada correctamente" : "Subzona agregada correctamente");
          this.cerrarDialogo();
          this.listarSubZonas();
        })
        .catch(error => this.showError(error.response?.data?.message || "Error al procesar la operación"))
        .finally(() => { this.loading = false; });
    },

    /**
     * Método para abrir el diálogo de confirmación de cambio de estado.
     * @param {Object} item - La subzona cuyo estado se desea cambiar.
     */
    abrirDialogoEstado(item) {
      this.dialogEstado = true;
      this.subZonaSeleccionada = { ...item };
      this.mensajeEstado = item.eliminado ? '¿Desea activar la subzona?' : '¿Desea desactivar la subzona?';
    },

    /**
     * Método para cambiar el estado de una subzona (activar/inactivar).
     * Actualiza solo el campo 'eliminado' sin modificar otros datos.
     */
    cambiarEstado() {
      const payload = {
        idSubZona: this.subZonaSeleccionada.idSubZona,
        idEmpresa: this.enterprise.code,
        nombreSubZona: this.subZonaSeleccionada.nombreSubZona,
        tipoSubZona: this.subZonaSeleccionada.tipoSubZona,
        codigoBodega: this.subZonaSeleccionada.codigoBodega,
        idZona: this.subZonaSeleccionada.idZona,
        eliminado: !this.subZonaSeleccionada.eliminado,
      };

      this.$http.put("msa-administration/api/subzonas/actualizar", payload, {
        params: { actUsua: this.usuarioLogueado }
      })
        .then(() => {
          this.showSuccess("Estado actualizado correctamente");
          this.dialogEstado = false;
          this.listarSubZonas();
        })
        .catch(error => this.showError(error.response?.data?.message || "No se pudo cambiar el estado"))
    },

    /**
     * Método para limpiar el formulario y restablecer el estado del componente.
     * Resetea todos los valores a su estado inicial.
     */
    clear() {
      this.subZonaSeleccionada = {
        idSubZona: null, nombreSubZona: "", tipoSubZona: null,
        codigoBodega: null, idZona: null, eliminado: false
      };
      this.listaZonas = [];
      if (this.$refs.formSubZona) this.$refs.formSubZona.resetValidation();
    },

    //Método para cerrar el diálogo de creación/edición.
    cerrarDialogo() {
      this.dialogoSubZona = false;
      this.clear();
      this.listarBodegas();
    }
  }
};
</script>

<!-- #################################################################################### -->
<!-- ###### Sección de Style                                                       ###### -->
<!-- #################################################################################### -->
<style scoped>
.contenido {
  padding: 1rem;
  width: 100%;
  height: 86vh;
}

.titulo {
  background-color: #1867c0;
  color: white;
}

.espaciado {
  padding-top: 20px !important;
}

.filtro {
  width: 25%;
}

::v-deep .elevation .v-data-footer {
  width: 100%;
}

::v-deep .elevation div table thead tr th {
  background-color: rgb(223, 223, 223) !important;
}

::v-deep .elevation div table thead tr th span {
  font-weight: bold;
  color: black !important;
}

::v-deep .v-data-table tbody tr {
  height: 50px;
}

::v-deep .v-data-table tbody td {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}
</style>